import React, { useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from '@mui/material'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ScienceIcon from '@mui/icons-material/Science'
import { ITestUser } from '@/api/services/DeveloperService'
import { cardSx, sectionTitleSx, tableHeadCellSx } from '../styles'

const SCENARIO_TAG_COLOURS: Record<
  string,
  'default' | 'primary' | 'warning' | 'error' | 'success'
> = {
  blank: 'default',
  bank_connected: 'primary',
  active_loan: 'success',
  pending_application: 'warning',
  declined: 'error',
  custom: 'default',
}

interface Props {
  testUsers: ITestUser[]
  isLoading: boolean
  isDeleting: boolean
  onDelete: (userId: string) => void
}

export const TestUsersList: React.FC<Props> = ({ testUsers, isLoading, isDeleting, onDelete }) => {
  const [copiedId, setCopiedId] = useState<string | null>(null)

  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedId(id)
      setTimeout(() => setCopiedId(null), 1500)
    } catch (error) {
      console.error('Failed to copy text to clipboard', error)
    }
  }

  return (
    <Box>
      <Box sx={{ ...cardSx, border: '1px solid', borderColor: '#e0d7e4', borderRadius: 3 }}>
        <Box sx={{ p: 3 }}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="subtitle1" sx={sectionTitleSx}>
              Test users
            </Typography>
            <Chip
              label={isLoading ? '…' : `${testUsers.length} users`}
              size="small"
              variant="outlined"
              sx={{ fontWeight: 500, borderColor: '#cac0cf', color: '#4b2b5c' }}
            />
          </Box>

          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} sx={{ color: '#fa624b' }} />
            </Box>
          ) : testUsers.length === 0 ? (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, gap: 1 }}
            >
              <ScienceIcon sx={{ fontSize: 40, color: '#e0d7e4' }} />
              <Typography variant="body2" color="#4b2b5c">
                No test users yet
              </Typography>
              <Typography variant="caption" color="#a08fa8">
                Create your first one using the form
              </Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{ borderRadius: 2, maxHeight: 440, overflow: 'auto', borderColor: '#e0d7e4' }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Name / Email', 'Scenario', 'ID', 'Token', 'Actions'].map((h, i, arr) => (
                      <TableCell
                        key={h}
                        align={i === arr.length - 1 ? 'right' : 'left'}
                        sx={tableHeadCellSx}
                      >
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {testUsers.map(u => (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} color="#2c063f">
                          {u.firstName} {u.lastName}
                        </Typography>
                        <Typography variant="caption" color="#a08fa8">
                          {u.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={u.scenarioTag.replace(/_/g, ' ')}
                          size="small"
                          color={SCENARIO_TAG_COLOURS[u.scenarioTag] ?? 'default'}
                          sx={{ fontSize: 11, fontWeight: 500, textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              fontFamily: 'monospace',
                              bgcolor: '#f5f2f7',
                              color: '#4b2b5c',
                              px: 0.8,
                              py: 0.2,
                              borderRadius: 1,
                              fontSize: 11,
                            }}
                          >
                            {u.id?.slice(0, 8) ?? '—'}…
                          </Typography>
                          <Tooltip title={copiedId === u.id ? 'Copied!' : 'Copy full ID'}>
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(u.id, u.id)}
                              aria-label="Copy full ID"
                              sx={{ p: 0.3 }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 13, color: '#a08fa8' }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        {u.tokenStatus === 'pending' ? (
                          <CircularProgress size={14} sx={{ color: '#a08fa8' }} />
                        ) : u.tokenStatus === 'error' ? (
                          <Tooltip title="Failed to generate token">
                            <Typography variant="caption" color="error">
                              Failed
                            </Typography>
                          </Tooltip>
                        ) : u.token ? (
                          <Tooltip
                            title={copiedId === `token-${u.id}` ? 'Copied!' : 'Copy auth token'}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleCopy(u.token as string, `token-${u.id}`)}
                              aria-label="Copy auth token"
                              sx={{ p: 0.3 }}
                            >
                              <ContentCopyIcon sx={{ fontSize: 13, color: '#a08fa8' }} />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Typography variant="caption" color="#cac0cf">
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Delete test user">
                          <IconButton
                            size="small"
                            onClick={() => onDelete(u.id)}
                            disabled={isDeleting}
                            aria-label="Delete test user"
                            sx={{ color: '#d60000' }}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Box>
  )
}
